import * as React from 'react'
import * as Ariakit from '@ariakit/react'
import { DismissableLayer } from '@radix-ui/react-dismissable-layer'
import { clsx } from 'clsx'

import { useMergeRefs } from '../../../hooks/useMergeRefs'
import usePortalContainer from '../../../helpers/usePortalContainer'
import { useIsInDialog } from '../../Dialog'
import { useRichTextFieldContext } from '../RichTextField'
import { useInlineComboboxInput } from './useInlineComboboxInput'
import styles from './InlineCombobox.module.css'

type InlineComboboxRootProps = Omit<
  React.ComponentPropsWithoutRef<'span'>,
  'onChange' | 'defaultValue' | 'contentEditable'
> & {
  children: React.ReactNode
  trigger: string
  value?: string
  onValueChange?: (value: string) => void
}

type InlineComboboxContextValue = ReturnType<typeof useInlineComboboxInput> & {
  store: Ariakit.ComboboxStore
  trigger: string
  value: string
  setValue: (value: string) => void
  locked: boolean
  listId: string
}
const InlineComboboxContext =
  React.createContext<InlineComboboxContextValue | null>(null)
function useInlineComboboxContext(): InlineComboboxContextValue {
  const context = React.useContext(InlineComboboxContext)
  if (!context)
    throw new Error('InlineCombobox parts require InlineCombobox.Root.')
  return context
}

/**
 * The same resolve-and-close a consumer gets for free by clicking an
 * `InlineCombobox.Item` — removes the trigger node, defocuses the input,
 * refocuses the editable, then runs `callback`. Exposed for consumers that
 * need to resolve the combobox from something other than a rendered item
 * (e.g. an async side effect recognizing a pasted URL as a direct match).
 */
function useInlineComboboxSelect(): (callback: () => void) => void {
  const { select } = useInlineComboboxContext()
  return select
}

const InlineComboboxRoot = React.forwardRef<
  HTMLSpanElement,
  InlineComboboxRootProps
>(function InlineComboboxRoot(
  {
    trigger,
    value: controlledValue,
    onValueChange,
    children,
    className,
    ...props
  },
  ref,
) {
  const { disabled, readOnly } = useRichTextFieldContext()
  const locked = disabled || readOnly
  const [localValue, setLocalValue] = React.useState('')
  const value = controlledValue ?? localValue
  const setValue = React.useCallback(
    (next: string) => {
      if (locked) return
      setLocalValue(next)
      onValueChange?.(next)
    },
    [locked, onValueChange],
  )
  const lifecycle = useInlineComboboxInput({ trigger, value, locked })
  const listId = React.useId()
  const store = Ariakit.useComboboxStore({
    value,
    setValue,
    open: !locked && lifecycle.focused,
    virtualFocus: true,
    focusLoop: true,
    focusWrap: true,
    includesBaseElement: false,
    resetValueOnHide: false,
    resetValueOnSelect: false,
  })
  const items = Ariakit.useStoreState(store, 'renderedItems')
  const activeId = Ariakit.useStoreState(store, 'activeId')
  const previousQuery = React.useRef(value)
  React.useEffect(() => {
    const changedQuery = previousQuery.current !== value
    previousQuery.current = value
    if (
      changedQuery ||
      !items.some((item) => item.id === activeId && !item.disabled)
    ) {
      store.setActiveId(items.find((item) => !item.disabled)?.id ?? null)
    }
  }, [activeId, items, store, value])
  const context = React.useMemo(
    () => ({ ...lifecycle, store, trigger, value, setValue, locked, listId }),
    [lifecycle, store, trigger, value, setValue, locked, listId],
  )
  return (
    <InlineComboboxContext.Provider value={context}>
      <Ariakit.ComboboxProvider store={store}>
        <span
          {...props}
          ref={ref}
          className={clsx(styles.root, className)}
          contentEditable={false}
          data-inline-combobox=""
        >
          {children}
        </span>
      </Ariakit.ComboboxProvider>
    </InlineComboboxContext.Provider>
  )
})

type AccessibleName =
  | { 'aria-label': string; 'aria-labelledby'?: string }
  | { 'aria-label'?: string; 'aria-labelledby': string }
type InlineComboboxInputProps = Omit<
  React.ComponentPropsWithoutRef<'input'>,
  | 'value'
  | 'defaultValue'
  | 'disabled'
  | 'readOnly'
  | 'role'
  | 'type'
  | 'size'
  | 'autoFocus'
  | 'aria-controls'
  | 'aria-activedescendant'
  | 'aria-expanded'
  | 'aria-autocomplete'
  | 'aria-haspopup'
  | 'aria-disabled'
  | 'aria-readonly'
> &
  AccessibleName

const InlineComboboxInput = React.forwardRef<
  HTMLInputElement,
  InlineComboboxInputProps
>(function InlineComboboxInput(
  {
    className,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onKeyDownCapture,
    onCompositionStart,
    onCompositionEnd,
    ...props
  },
  ref,
) {
  const context = useInlineComboboxContext()
  const { disabled, readOnly } = useRichTextFieldContext()
  const mergedRef = useMergeRefs(ref, context.inputRef)
  return (
    <span className={styles.inputWrapper}>
      <span aria-hidden="true">{context.trigger}</span>
      <Ariakit.Combobox
        {...props}
        ref={mergedRef}
        className={clsx(styles.input, className)}
        store={context.store}
        type="text"
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Ariakit renders a native input with combobox semantics.
        role="combobox"
        aria-expanded={!context.locked && context.focused}
        autoFocus={false}
        disabled={false}
        value={context.value}
        size={Math.max(1, context.value.length)}
        readOnly={context.locked}
        aria-disabled={disabled || undefined}
        aria-readonly={readOnly || undefined}
        aria-haspopup="listbox"
        aria-controls={context.listId}
        autoSelect={false}
        autoComplete="list"
        setValueOnChange={false}
        setValueOnClick={false}
        showOnClick={false}
        showOnChange={false}
        showOnKeyPress={false}
        onChange={(event) => {
          if (!context.locked) {
            context.setValue(event.target.value)
            onChange?.(event)
          }
        }}
        onFocus={(event) => {
          context.resume()
          onFocus?.(event)
        }}
        onBlur={(event) => {
          context.setFocused(false)
          context.cancel()
          onBlur?.(event)
        }}
        onCompositionStart={(event) => {
          context.composing.current = true
          onCompositionStart?.(event)
        }}
        onCompositionEnd={(event) => {
          context.endComposition(event.currentTarget.value)
          onCompositionEnd?.(event)
        }}
        onKeyDownCapture={(event) => {
          if (context.composing.current || event.nativeEvent.isComposing) {
            event.stopPropagation()
            return
          }
          if (context.locked && event.key !== 'Tab') {
            if (disabled || event.key === 'Enter') event.preventDefault()
            event.stopPropagation()
            return
          }
          if (event.key === 'Enter') {
            event.preventDefault()
            event.stopPropagation()
            const { activeId } = context.store.getState()
            const item = activeId ? context.store.item(activeId) : undefined
            if (item && !item.disabled) item.element?.click()
            return
          }
          onKeyDownCapture?.(event)
        }}
        onKeyDown={(event) => {
          context.onKeyDown(event)
          if (!event.defaultPrevented) onKeyDown?.(event)
        }}
      />
    </span>
  )
})

type InlineComboboxContentProps = React.ComponentPropsWithoutRef<'div'> & {
  anchorRef?: React.RefObject<HTMLElement | null>
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
}
const InlineComboboxContent = React.forwardRef<
  HTMLDivElement,
  InlineComboboxContentProps
>(function InlineComboboxContent(
  {
    anchorRef,
    side = 'bottom',
    align = 'start',
    sideOffset = 8,
    className,
    ...props
  },
  ref,
) {
  const { store, inputRef, locked, focused } = useInlineComboboxContext()
  const customContainer = usePortalContainer()
  const { isInDialog, dialogRef } = useIsInDialog()
  const anchor = (anchorRef ?? inputRef).current
  const [portalElement, setPortalElement] =
    React.useState<HTMLDivElement | null>(null)
  React.useLayoutEffect(() => {
    if (!anchor || locked || !focused) return
    const container =
      isInDialog && dialogRef?.current
        ? dialogRef.current
        : (customContainer ?? anchor.ownerDocument.body)
    const portal = anchor.ownerDocument.createElement('div')
    container.appendChild(portal)
    setPortalElement(portal)
    return (): void => {
      portal.remove()
      setPortalElement(null)
    }
  }, [anchor, locked, focused, isInDialog, dialogRef, customContainer])
  React.useLayoutEffect(() => {
    store.setAnchorElement(anchor)
  }, [anchor, store])
  React.useLayoutEffect(() => {
    store.setState('placement', align === 'center' ? side : `${side}-${align}`)
  }, [align, side, store])
  if (locked || !focused || !anchor?.isConnected || !portalElement) return null
  return (
    // Ariakit's popover doesn't join Radix's dismissable-layer stack on its
    // own, so an ancestor Radix Dialog stays the "highest layer" and keeps
    // handling Escape itself — closing the whole dialog instead of just this
    // popover. Registering a layer here (no handlers needed) reclaims that
    // spot for as long as the popover is mounted, matching how a nested
    // Radix Popover would have behaved. asChild avoids adding a wrapper div:
    // the popover already portals its own DOM elsewhere, so a real element
    // here would land inline in the editor's DOM (invalid nesting inside a
    // paragraph) for no visual purpose.
    <DismissableLayer asChild>
      <Ariakit.ComboboxPopover
        {...props}
        ref={ref}
        store={store}
        className={clsx(styles.content, className)}
        role="presentation"
        portal
        portalElement={portalElement}
        modal={false}
        preserveTabOrder={false}
        autoFocusOnShow={false}
        autoFocusOnHide={false}
        hideOnEscape={false}
        hideOnInteractOutside={false}
        gutter={sideOffset}
        fitViewport
        flip
        shift={8}
      />
    </DismissableLayer>
  )
})

type LayoutProps = React.ComponentPropsWithoutRef<'div'>
type InlineComboboxListProps = Omit<LayoutProps, 'id' | 'role' | 'tabIndex'> &
  AccessibleName
type InlineComboboxItemProps = Omit<
  LayoutProps,
  'onSelect' | 'role' | 'tabIndex'
> & {
  value: string
  textValue: string
  disabled?: boolean
  onSelect: () => void
  children: React.ReactNode
}
type InlineComboboxGroupProps = LayoutProps
type InlineComboboxGroupLabelProps = LayoutProps
type InlineComboboxStatusProps = Omit<
  LayoutProps,
  'role' | 'aria-live' | 'aria-atomic'
>

const InlineComboboxList = React.forwardRef<
  HTMLDivElement,
  InlineComboboxListProps
>(function InlineComboboxList({ className, ...props }, ref) {
  const { listId } = useInlineComboboxContext()
  return (
    <Ariakit.ComboboxList
      {...props}
      ref={ref}
      id={listId}
      className={clsx(styles.listbox, className)}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Ariakit coordinates this custom listbox with the native input.
      role="listbox"
      tabIndex={-1}
    />
  )
})
const InlineComboboxItem = React.forwardRef<
  HTMLDivElement,
  InlineComboboxItemProps
>(function InlineComboboxItem(
  {
    value,
    textValue,
    disabled,
    onSelect,
    onPointerDown,
    onMouseDown,
    onClick,
    className,
    ...props
  },
  ref,
) {
  const { select, locked, store } = useInlineComboboxContext()
  const generatedId = React.useId()
  const id = props.id ?? generatedId
  const active = Ariakit.useStoreState(store, 'activeId') === id
  return (
    <Ariakit.ComboboxItem
      {...props}
      ref={ref}
      value={value}
      id={id}
      aria-selected={active}
      aria-label={textValue}
      disabled={disabled || locked}
      className={clsx(styles.option, className)}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Virtual focus requires an Ariakit option.
      role="option"
      tabIndex={-1}
      setValueOnClick={false}
      selectValueOnClick={false}
      hideOnClick={false}
      onPointerDown={onPointerDown}
      onMouseDown={onMouseDown}
      onClick={(event) => {
        if (!disabled && !locked) {
          select(onSelect)
          onClick?.(event)
        }
      }}
    />
  )
})
const InlineComboboxGroup = React.forwardRef<
  HTMLDivElement,
  InlineComboboxGroupProps
>(function InlineComboboxGroup({ className, ...props }, ref) {
  return (
    <Ariakit.ComboboxGroup
      {...props}
      ref={ref}
      className={clsx(styles.group, className)}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Ariakit connects this group to its composed label.
      role="group"
    />
  )
})
const InlineComboboxGroupLabel = React.forwardRef<
  HTMLDivElement,
  InlineComboboxGroupLabelProps
>(function InlineComboboxGroupLabel({ className, ...props }, ref) {
  return (
    <Ariakit.ComboboxGroupLabel
      {...props}
      ref={ref}
      className={clsx(styles.groupLabel, className)}
    />
  )
})
const InlineComboboxStatus = React.forwardRef<
  HTMLDivElement,
  InlineComboboxStatusProps
>(function InlineComboboxStatus({ className, ...props }, ref) {
  return (
    <div
      {...props}
      ref={ref}
      className={clsx(styles.status, className)}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The live region announces messages outside the listbox.
      role="status"
      aria-live="polite"
      aria-atomic="true"
    />
  )
})

const InlineCombobox = {
  Root: InlineComboboxRoot,
  Input: InlineComboboxInput,
  Content: InlineComboboxContent,
  List: InlineComboboxList,
  Item: InlineComboboxItem,
  Group: InlineComboboxGroup,
  GroupLabel: InlineComboboxGroupLabel,
  Status: InlineComboboxStatus,
}
export { InlineCombobox, useInlineComboboxSelect }
export type {
  InlineComboboxRootProps,
  InlineComboboxInputProps,
  InlineComboboxContentProps,
  InlineComboboxListProps,
  InlineComboboxItemProps,
  InlineComboboxGroupProps,
  InlineComboboxGroupLabelProps,
  InlineComboboxStatusProps,
}
